import { Grid, Typography } from '@mui/material';
import { Identifier, MatchPlayerRole, MatchPlayerStatus } from '@okletsplay/core';
import { sessionSelector } from 'app/store/session/selectors';
import classNames from 'classnames';
import { MatchPlayer, MatchResultEntity } from 'app/interfaces/entity';
import { PlayerTile } from 'modules/users/components/PlayerTile/PlayerTile';
import React, { FC, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HumanizeOrdinalFormat } from 'shared/components/Format/HumanizeOrdinalFormat';
import { ValueOrDefaultFormat } from 'shared/components/Format/ValueOrDefaultFormat';
import { useStyles } from './ModalLeaderboard.styles';
import { acceptJoin, declineJoin } from 'modules/matches/store/current-match/repository';
import { MatchPlayerJoinRequestActions } from 'modules/matches/components/MatchModal/MatchPlayerJoinRequestActions';
import { MutedText } from 'shared/components/Format/Currency';

interface MatchLeaderboardProps {
  matchId: string;
  players: MatchPlayer[] | undefined;
  matchResults?: Record<Identifier, MatchResultEntity>;
}

export const MatchLeaderboard: FC<MatchLeaderboardProps> = ({
  matchId,
  players = [],
  matchResults = {},
}) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const currentUser = useSelector(sessionSelector.activeUser);

  const isPlayerReadyOrPlayed = useCallback(
    (player) => [MatchPlayerStatus.Ready, MatchPlayerStatus.Played].includes(player.status),
    []
  );

  const sortedPlayers = useMemo(() => {
    return players.sort((first, second) => {
      const isFirstReady = isPlayerReadyOrPlayed(first);
      const isSecondReady = isPlayerReadyOrPlayed(second);
      if (isFirstReady && !isSecondReady) return -1;
      if (!isFirstReady && isSecondReady) return 1;
      if (!isFirstReady && !isSecondReady) return 0;

      const firstScore = matchResults?.[first?.user?.userId]?.score ?? 0;
      const secondScore = matchResults?.[second?.user?.userId]?.score ?? 0;

      return secondScore - firstScore;
    });
  }, [players, isPlayerReadyOrPlayed, matchResults]);

  const isPlayerRequested = useCallback(
    (player) => [MatchPlayerStatus.Requested].includes(player.status),
    []
  );

  const isPlayerInvited = useCallback(
    (player) => [MatchPlayerStatus.Invited].includes(player.status),
    []
  );

  const isAnyPendingPlayer = useMemo(
    () => players.some((player) => isPlayerRequested(player) || isPlayerInvited(player)),
    [isPlayerInvited, isPlayerRequested, players]
  );

  const isCurrentPlayerOwner = useMemo(
    () =>
      players.findIndex(
        (player) => player.userId === currentUser.userId && player.role === MatchPlayerRole.Owner
      ) !== -1,
    [currentUser.userId, players]
  );

  const onAcceptHandler = useCallback(
    (userId: Identifier) => {
      dispatch(acceptJoin({ matchId, userId }));
    },
    [dispatch, matchId]
  );

  const onRejectHandler = useCallback(
    (userId: Identifier) => {
      dispatch(declineJoin({ matchId, userId }));
    },
    [dispatch, matchId]
  );

  return (
    <>
      <Grid container spacing={0} className={classes.grid}>
        <Grid item xs={5}>
          <Typography>Leaderboard</Typography>
        </Grid>
        <Grid item xs={2} className={classes.justifyCenter}>
          <Typography>Est. Winning</Typography>
        </Grid>
        <Grid item xs={2} className={classes.justifyCenter}>
          <Typography>Score</Typography>
        </Grid>
        {isAnyPendingPlayer && isCurrentPlayerOwner ? (
          <Grid item xs={3}>
            <Typography>Actions</Typography>
          </Grid>
        ) : null}
      </Grid>
      {sortedPlayers.map((player, index) => (
        <Grid
          key={player.userId}
          container
          spacing={0}
          className={classNames(classes.grid, {
            [classes.currentPlayer]: player?.user?.id === currentUser?.id,
            [classes.notReadyPlayer]: !isPlayerReadyOrPlayed(player),
          })}
        >
          <Grid item xs={1}>
            <Typography>
              <HumanizeOrdinalFormat
                order={index + 1}
                hideSuffix={!isPlayerReadyOrPlayed(player)}
              />
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <PlayerTile user={player.user} readonly />
          </Grid>
          <Grid item xs={2} className={classes.justifyCenter}>
            <Typography>—</Typography>
          </Grid>
          <Grid item xs={2} className={classes.justifyCenter}>
            <Typography>
              <ValueOrDefaultFormat value={matchResults?.[player?.user?.userId]?.score} />
            </Typography>
          </Grid>
          {isPlayerRequested(player) && isCurrentPlayerOwner ? (
            <Grid item xs={3}>
              <MatchPlayerJoinRequestActions
                userId={player.user?.userId}
                onAccept={onAcceptHandler}
                onReject={onRejectHandler}
              />
            </Grid>
          ) : null}

          {isPlayerRequested(player) && !isCurrentPlayerOwner ? (
            <Grid item xs={3}>
              <MutedText>Join request sent</MutedText>
            </Grid>
          ) : null}

          {isPlayerInvited(player) ? (
            <Grid item xs={3}>
              <MutedText>Invitation sent</MutedText>
            </Grid>
          ) : null}
        </Grid>
      ))}
    </>
  );
};
